<template>
  <div class="multiplewayToggle">
    <div class="multiplewayToggle__display">
      <div class="multiplewayToggle__background" :style="toggleStyle" />
      <div
        v-for="(option, index) in props.options"
        :key="index"
        class="multiplewayToggle__button"
        :style="toggleStyle"
        @click="handleToggle(index)"
        @keypress="handleToggle(index)"
      >
        <component
          :is="props.optionsHasIcon ? getIcon(index) : ''"
          v-if="props.optionsHasIcon"
        />
        <p>{{ option }}</p>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, defineAsyncComponent, ref } from 'vue';

const props = defineProps({
  checked: {
    type: Boolean,
  },
  options: {
    type: Array,
    default() {
      return ['First option', 'Second option', 'Third option'];
    },
  },
  optionsHasIcon: {
    type: Boolean,
    required: false,
    default: false,
  },
  optionsIcons: {
    type: Array,
    required: false,
    default() {
      return [];
    },
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
});

const emit = defineEmits(['update:toggleValue']);
const selectedOption = ref(1);

const handleToggle = (selectedIndex) => {
  selectedOption.value = selectedIndex + 1;
  emit('update:toggleValue', selectedIndex);
};

const getIcon = (index) => {
  if (!props.optionsHasIcon) {
    return null;
  }

  return defineAsyncComponent(() => import(
      /* @vite-ignore */ `../../../components/icons/${props.optionsIcons[index]}.vue`
    ));
};

const toggleStyle = computed(() => ({
  '--toggle-button-width': `${100 / props.options.length}%`,
  '--toggle-selected-option': `${(selectedOption.value - 1) * 100}%`,
}));
</script>
<style lang="scss">
.multiplewayToggle {
  width: 100%;
  display: block;
  cursor: pointer;
  margin-bottom: 3.2rem;

  input[type='checkbox'] {
    opacity: 0;
    position: absolute;
  }

  &__display {
    display: flex;
    border-radius: $border-radius;
    position: relative;
    overflow: hidden;
    background: var(--input-background);
  }

  &__button {
    width: var(--toggle-button-width);
    padding: 2rem;
    text-align: center;
    position: relative;
    transition: opacity 250ms;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: $padding-sml;
    transition: var(--toggle-icon-transition);

    &--first {
      .second & {
        p {
          color: var(--toggle-text-color-second);
        }

        path {
          stroke: var(--toggle-text-color-second);
          transition: var(--toggle-icon-transition);
        }
      }

      .first & {
        p {
          color: $black;
          color: var(--toggle-text-color-first);
        }

        path {
          stroke: var(--toggle-text-color-first);
          transition: var(--toggle-icon-transition);
        }
      }
    }

    &--second {
      .first & {
        p {
          color: var(--toggle-text-color-second);
        }

        path {
          stroke: var(--toggle-text-color-second);
          transition: var(--toggle-icon-transition);
        }
      }

      .second & {
        p {
          color: var(--toggle-text-color-first);
        }

        path {
          stroke: var(--toggle-text-color-first);
          transition: var(--toggle-icon-transition);
        }
      }
    }

    p {
      font-weight: 500;
    }
  }

  &__background {
    width: var(--toggle-button-width);
    height: 100%;
    position: absolute;
    background: var(--gradient);
    z-index: 0;
    transition: var(--toggle-background-transition);
    transform: translateX(var(--toggle-selected-option));
  }
}
</style>
